'use client';

import { useState, useRef } from 'react';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from '@/hooks/useTranslations';
import { CreateInvoiceSchema } from '@/schemas';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormError } from '@/components/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { LoadingButton } from '@/components/loading-button';
import { Items } from '../items';
import { InvoiceType } from '../invoice-type';
import { SingleDatePicker } from '../single-date-picker';
import { DatePickerWithRange } from '../date-range';
import { ImportFileList } from './import-file-list';
import { useInvoiceContext } from '@/contexts/invoice-context';
import { useSubmitStatus } from '@/hooks/useSubmitStatus';
import { useSession } from 'next-auth/react';
import { calculateInvoiceTotals } from '@/lib/helpers/invoice-math';
import { getCurrencyFormatter } from '@/lib/currency-formatter';
import { importInvoice, processInvoiceOCR, uploadInvoicePdf } from '@/actions';
import { pdfPagesToBase64, pdfToText } from '@/lib/pdf-to-image';
import { ImportQueueItem, InvoiceOCRResult } from '@/types/invoice-import';
import { TItems } from '@/types/invoice';
import {
  Upload,
  FileImage,
  Loader2,
  CheckCircle,
  SkipForward,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { addDays } from 'date-fns';
import { toast } from 'sonner';

type FormValues = z.infer<typeof CreateInvoiceSchema>;

const EMPTY_ITEM: TItems = {
  description: '',
  qty: 0,
  unit: '',
  price: 0,
  tax: 0,
  total: 0,
};

export const ImportInvoiceForm = () => {
  const t = useTranslations('ImportInvoice');
  const createT = useTranslations('CreateInvoicePopup');
  const { dispatch } = useInvoiceContext();
  const session = useSession();

  // Phase & queue
  const [phase, setPhase] = useState<'upload' | 'review' | 'done'>('upload');
  const [queue, setQueue] = useState<ImportQueueItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [savedCount, setSavedCount] = useState(0);

  // Review form state — refs keep values fresh for async callbacks
  const [error, setError] = useState<string>();
  const { status, wrap } = useSubmitStatus();
  const [items, setItems] = useState<TItems[]>([EMPTY_ITEM]);
  const [clientData, setClientData] = useState({
    name: '',
    address: '',
    city: '',
    country: '',
    zipCode: '',
    taxNumber: '',
    registrationNumber: '',
  });
  const [saveToProducts, setSaveToProducts] = useState(false);
  const [useOriginalNumber, setUseOriginalNumber] = useState(false);
  const [originalInvoiceNumber, setOriginalInvoiceNumber] = useState('');

  // Single ref object to avoid stale closures in async handlers
  const ref = useRef({
    queue: [] as ImportQueueItem[],
    currentIndex: -1,
    items: [EMPTY_ITEM] as TItems[],
    clientData: {
      name: '',
      address: '',
      city: '',
      country: '',
      zipCode: '',
      taxNumber: '',
      registrationNumber: '',
    },
    saveToProducts: false,
    useOriginalNumber: false,
    originalInvoiceNumber: '',
  });

  // Sync helpers — update both state (for render) and ref (for callbacks)
  const sync = {
    queue(updater: (prev: ImportQueueItem[]) => ImportQueueItem[]) {
      setQueue((prev) => {
        const next = updater(prev);
        ref.current.queue = next;
        return next;
      });
    },
    currentIndex(v: number) {
      setCurrentIndex(v);
      ref.current.currentIndex = v;
    },
    items(v: TItems[]) {
      setItems(v);
      ref.current.items = v;
    },
    clientData(v: typeof clientData) {
      setClientData(v);
      ref.current.clientData = v;
    },
    saveToProducts(v: boolean) {
      setSaveToProducts(v);
      ref.current.saveToProducts = v;
    },
    useOriginalNumber(v: boolean) {
      setUseOriginalNumber(v);
      ref.current.useOriginalNumber = v;
    },
    originalInvoiceNumber(v: string) {
      setOriginalInvoiceNumber(v);
      ref.current.originalInvoiceNumber = v;
    },
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(CreateInvoiceSchema),
    defaultValues: {
      clientId: 'import-placeholder',
      invoiceNumber: 'auto',
      invoiceType: 'Invoice',
      status: 'PAID',
      ddvAdditionalInput: '',
      isRecurring: false,
      serviceDate: { from: undefined, to: undefined },
      issuedDate: new Date(),
      dueDate: addDays(new Date(), 15),
    },
  });

  // Load OCR result into form fields
  const loadOCRIntoForm = (ocr: InvoiceOCRResult) => {
    sync.clientData({
      name: ocr.client.name || '',
      address: ocr.client.address || '',
      city: ocr.client.city || '',
      country: ocr.client.country || '',
      zipCode: ocr.client.zipCode || '',
      taxNumber: ocr.client.taxNumber || '',
      registrationNumber: ocr.client.registrationNumber || '',
    });
    form.setValue('invoiceType', ocr.invoiceType || 'Invoice');
    form.setValue('status', ocr.status || 'PAID');
    form.setValue('ddvAdditionalInput', ocr.ddvAdditionalInput || '');
    sync.originalInvoiceNumber(ocr.originalInvoiceNumber || '');
    if (ocr.issuedDate) form.setValue('issuedDate', new Date(ocr.issuedDate));
    if (ocr.dueDate) form.setValue('dueDate', new Date(ocr.dueDate));
    if (ocr.serviceFrom) {
      form.setValue('serviceDate', {
        from: new Date(ocr.serviceFrom),
        to: ocr.serviceTo ? new Date(ocr.serviceTo) : undefined,
      });
    }
    sync.items(ocr.items?.length ? ocr.items : [EMPTY_ITEM]);
    setError('');
  };

  // Convert file to content for OCR
  const extractFileContent = async (file: File) => {
    if (file.type === 'application/pdf') {
      let text: string | undefined;
      try {
        text = await pdfToText(file, 3);
      } catch {
        /* fall back to image */
      }
      const images = await pdfPagesToBase64(file, 3);
      return { images, text };
    }
    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(file);
    });
    return { images: [base64], text: undefined };
  };

  // Process a single file: upload + OCR
  const processFile = async (queueItem: ImportQueueItem, index: number) => {
    sync.queue((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, status: 'processing' as const } : item
      )
    );

    try {
      const formData = new FormData();
      formData.append('file', queueItem.file);
      const uploadResult = await uploadInvoicePdf(formData);
      const pdfUrl = uploadResult.success || undefined;

      const { images, text } = await extractFileContent(queueItem.file);
      const ocrResult = await processInvoiceOCR(images, text);

      sync.queue((prev) =>
        prev.map((item, i) =>
          i === index
            ? { ...item, status: 'ready' as const, pdfUrl, ocrResult }
            : item
        )
      );
    } catch {
      sync.queue((prev) =>
        prev.map((item, i) =>
          i === index
            ? {
                ...item,
                status: 'error' as const,
                error: 'Failed to process file',
              }
            : item
        )
      );
    }
  };

  // Handle file drop/select
  const handleFiles = async (files: FileList | File[]) => {
    const valid = Array.from(files).filter(
      (f) =>
        (f.type.startsWith('image/') || f.type === 'application/pdf') &&
        f.size <= 10 * 1024 * 1024
    );
    if (!valid.length) return;

    const newItems: ImportQueueItem[] = valid.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      fileName: file.name,
      file,
      status: 'pending' as const,
    }));

    const startIdx = ref.current.queue.length;
    sync.queue((prev) => [...prev, ...newItems]);
    setIsProcessing(true);
    await Promise.all(
      newItems.map((item, i) => processFile(item, startIdx + i))
    );
    setIsProcessing(false);
  };

  // Find next ready item after given index (wraps around)
  const findNextReady = (afterIdx: number): number => {
    const q = ref.current.queue;
    const after = q.findIndex(
      (item, i) => i > afterIdx && item.status === 'ready'
    );
    if (after !== -1) return after;
    return q.findIndex((item, i) => i !== afterIdx && item.status === 'ready');
  };

  // Navigate to a queue item and load its OCR data
  const goTo = (index: number) => {
    const q = ref.current.queue;
    if (q[index]?.status !== 'ready') return;
    sync.currentIndex(index);
    loadOCRIntoForm(q[index].ocrResult!);
  };

  const startReview = () => {
    const first = ref.current.queue.findIndex(
      (item) => item.status === 'ready'
    );
    if (first === -1) return;
    sync.currentIndex(first);
    setPhase('review');
    loadOCRIntoForm(ref.current.queue[first].ocrResult!);
  };

  const handleSkip = () => {
    const next = findNextReady(ref.current.currentIndex);
    if (next !== -1) goTo(next);
  };

  const handleImport = () => {
    setError('');
    const values = form.getValues();
    const { clientData: client, items: itms, currentIndex: idx } = ref.current;
    const {
      saveToProducts: saveProd,
      useOriginalNumber: useOrig,
      originalInvoiceNumber: origNum,
    } = ref.current;

    if (!client.name.trim()) {
      toast.error('Client name is required.');
      return setError('Client name is required.');
    }
    if (!itms.some((i) => i.description.trim())) {
      toast.error('At least one item with a description is required.');
      return setError('At least one item with a description is required.');
    }

    wrap(() =>
      importInvoice({
        client,
        invoiceType: values.invoiceType,
        status: values.status,
        serviceFrom: values.serviceDate.from || new Date(),
        serviceTo: values.serviceDate.to || null,
        issuedDate: values.issuedDate || new Date(),
        dueDate: values.dueDate,
        items: itms,
        ddvAdditionalInput: values.ddvAdditionalInput,
        saveToProducts: saveProd,
        useOriginalInvoiceNumber: useOrig,
        originalInvoiceNumber: origNum,
      }).then((data) => {
        if (!('success' in data)) {
          const msg = data.error || 'Something went wrong!';
          toast.error(msg);
          setError(msg);
          throw new Error(msg);
        }
        toast.success(data.success);
        sync.queue((prev) =>
          prev.map((item, i) =>
            i === idx ? { ...item, status: 'saved' as const } : item
          )
        );
        setSavedCount((prev) => prev + 1);
        const next = findNextReady(idx);
        if (next !== -1) goTo(next);
        else setPhase('done');
      })
    );
  };

  const readyCount = queue.filter((i) => i.status === 'ready').length;
  const { subtotal, tax, total } = calculateInvoiceTotals(items);
  const currency = session.data?.user.currency;

  // --- PHASE: DONE ---
  if (phase === 'done') {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <CheckCircle className="h-16 w-16 text-green-500" />
        <h3 className="text-lg font-semibold">{t('importComplete')}</h3>
        <p className="text-sm text-muted-foreground">
          {t('importedCount', { count: String(savedCount) })}
        </p>
        <Button
          onClick={() => dispatch({ type: 'CLOSE_IMPORT_INVOICE_POPUP' })}
        >
          {t('close')}
        </Button>
      </div>
    );
  }

  // --- PHASE: UPLOAD ---
  if (phase === 'upload') {
    return (
      <div className="space-y-6">
        <div
          className={cn(
            'relative border-2 border-dashed rounded-lg p-8 transition-colors',
            isDragging
              ? 'border-primary bg-primary/5'
              : 'border-gray-300 dark:border-gray-700'
          )}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
            handleFiles(e.dataTransfer.files);
          }}
        >
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800">
              <FileImage className="h-8 w-8 text-gray-400" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium">{t('dropZoneTitle')}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('dropZoneSubtitle')}
              </p>
            </div>
            <label>
              <input
                type="file"
                accept="image/*,application/pdf"
                multiple
                onChange={(e) => {
                  if (e.target.files) handleFiles(e.target.files);
                  e.target.value = '';
                }}
                className="hidden"
              />
              <Button type="button" variant="outline" size="sm" asChild>
                <span>
                  <Upload className="h-4 w-4 mr-2" />
                  {t('selectFiles')}
                </span>
              </Button>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('fileTypes')}
            </p>
          </div>
        </div>

        {queue.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium">
              {t('filesUploaded', { count: String(queue.length) })}
            </h4>
            <ImportFileList
              queue={queue}
              currentIndex={-1}
              onSelect={() => {}}
            />
            {isProcessing && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('processingFiles')}
              </div>
            )}
            {!isProcessing && readyCount > 0 && (
              <Button onClick={startReview} className="w-full">
                {t('reviewInvoices', { count: String(readyCount) })}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  // --- PHASE: REVIEW ---
  return (
    <div className="space-y-4">
      {queue.length > 1 && (
        <div className="border rounded-lg p-3">
          <h4 className="text-sm font-medium mb-2">
            {t('invoiceCounter', {
              current: String(
                queue.filter((i) => i.status === 'saved').length + 1
              ),
              total: String(queue.length),
            })}
          </h4>
          <ImportFileList
            queue={queue}
            currentIndex={currentIndex}
            onSelect={goTo}
          />
        </div>
      )}

      <Form {...form}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleImport();
          }}
          className="space-y-6"
        >
          {/* Client */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold">{t('clientInfo')}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('clientName')}</label>
                <Input
                  value={clientData.name}
                  onChange={(e) =>
                    sync.clientData({ ...clientData, name: e.target.value })
                  }
                  placeholder={t('clientName')}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  {t('clientTaxNumber')}
                </label>
                <Input
                  value={clientData.taxNumber}
                  onChange={(e) =>
                    sync.clientData({
                      ...clientData,
                      taxNumber: e.target.value,
                    })
                  }
                  placeholder={t('clientTaxNumber')}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">
                {t('clientAddress')}
              </label>
              <Input
                value={clientData.address}
                onChange={(e) =>
                  sync.clientData({ ...clientData, address: e.target.value })
                }
                placeholder={t('clientAddress')}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('clientCity')}</label>
                <Input
                  value={clientData.city}
                  onChange={(e) =>
                    sync.clientData({ ...clientData, city: e.target.value })
                  }
                  placeholder={t('clientCity')}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  {t('clientCountry')}
                </label>
                <Input
                  value={clientData.country}
                  onChange={(e) =>
                    sync.clientData({ ...clientData, country: e.target.value })
                  }
                  placeholder={t('clientCountry')}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  {t('clientZipCode')}
                </label>
                <Input
                  value={clientData.zipCode}
                  onChange={(e) =>
                    sync.clientData({ ...clientData, zipCode: e.target.value })
                  }
                  placeholder={t('clientZipCode')}
                />
              </div>
            </div>
          </div>

          <div className="border-b" />

          {/* Type & Status */}
          <div className="flex justify-between gap-2 flex-col sm:flex-row">
            <FormField
              control={form.control}
              name="invoiceType"
              render={({ field }) => (
                <FormItem className="flex flex-col w-full sm:w-1/2">
                  <FormLabel>{createT('type')}</FormLabel>
                  <FormControl>
                    <InvoiceType
                      placeholder={createT('selectType')}
                      value={field.value}
                      onSelect={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem className="flex flex-col w-full sm:w-1/2">
                  <FormLabel>{t('status')}</FormLabel>
                  <FormControl>
                    <select
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      <option value="PAID">{t('paid')}</option>
                      <option value="UNPAID">{t('unpaid')}</option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Invoice number */}
          <div className="flex flex-col gap-3 p-3 border rounded-lg bg-muted/30">
            <div className="flex flex-row items-center justify-between">
              <label className="text-sm font-medium">
                {t('useOriginalNumber')}
              </label>
              <Switch
                checked={useOriginalNumber}
                onCheckedChange={sync.useOriginalNumber}
                disabled={status === 'loading'}
              />
            </div>
            {useOriginalNumber ? (
              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  {t('originalNumber')}
                </label>
                <Input
                  value={originalInvoiceNumber}
                  onChange={(e) => sync.originalInvoiceNumber(e.target.value)}
                  disabled={status === 'loading'}
                  placeholder={t('originalNumberPlaceholder')}
                />
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t('autoNumberHint')}
              </p>
            )}
          </div>

          {/* Dates */}
          <FormField
            control={form.control}
            name="serviceDate"
            render={({ field }) => (
              <FormItem className="flex flex-col w-full">
                <FormLabel>{createT('dateOfServicePerformed')}</FormLabel>
                <FormControl>
                  <DatePickerWithRange
                    value={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-between gap-2 flex-col sm:flex-row">
            <FormField
              control={form.control}
              name="dueDate"
              render={({ field }) => (
                <FormItem className="flex flex-col w-full sm:w-1/2">
                  <FormLabel>{createT('dueDate')}</FormLabel>
                  <FormControl>
                    <SingleDatePicker
                      id="import-due-date"
                      ariaLabel="Due date"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="issuedDate"
              render={({ field }) => (
                <FormItem className="flex flex-col w-full sm:w-1/2">
                  <FormLabel>{createT('issuedDate')}</FormLabel>
                  <FormControl>
                    <SingleDatePicker
                      id="import-issued-date"
                      ariaLabel="Issued date"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="border-b" />

          <Items
            status={status}
            items={items}
            setItems={sync.items}
            currency={currency}
          />

          <div className="border-b" />

          <FormField
            control={form.control}
            name="ddvAdditionalInput"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{createT('ddvAdditionalInputLabel')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={status === 'loading'}
                    placeholder={createT('ddvAdditionalInputPlaceholder')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="border-b" />

          <div className="flex flex-row items-center justify-between">
            <label className="text-sm font-medium">{t('saveToProducts')}</label>
            <Switch
              checked={saveToProducts}
              onCheckedChange={sync.saveToProducts}
              disabled={status === 'loading'}
            />
          </div>

          <div className="border-b" />

          {/* Totals */}
          <div className="p-3">
            <div className="flex justify-between">
              <h6>{createT('subTotal')}</h6>
              <span>{getCurrencyFormatter(subtotal, currency)}</span>
            </div>
            <div className="flex justify-between">
              <h6>{createT('tax')}:</h6>
              <span>{getCurrencyFormatter(tax, currency)}</span>
            </div>
            <div className="flex justify-between">
              <h6>{createT('total')}:</h6>
              <span>{getCurrencyFormatter(total, currency)}</span>
            </div>
          </div>

          <FormError message={error} />

          <div className="flex gap-2">
            {readyCount > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={handleSkip}
                disabled={status === 'loading'}
              >
                <SkipForward className="h-4 w-4 mr-2" />
                {t('skip')}
              </Button>
            )}
            <LoadingButton
              isFull
              type="submit"
              status={status}
              idleText={t('importInvoice')}
            />
          </div>
        </form>
      </Form>
    </div>
  );
};
